import { Color, createTheme, Theme, ThemeOptions } from '@mui/material';
import { deepmerge } from '@mui/utils';

import { defaultTheme } from '../default-theme';
import { getPalette } from '../helpers';

const grey: Color = {
  50: '#EFECEA',
  100: '#EFECEA',
  200: '#DDDDDD',
  300: '#939CA6',
  400: '#B6B6B6',
  500: '#A3A7AB',
  600: '#87888B',
  700: '#7F8083',
  800: '#62666C',
  900: '#393C42',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const blue: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#87C8FF',
  600: '#6CA5D6',
  700: '#6799C3',
  800: '#527A9C',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const mapBlue: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#7591AD',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const green: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#AFFB74',
  600: '#1CBD07',
  700: '#83BC62',
  800: '#587E3A',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const orange: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#FF975D',
  600: '#E87636',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const violet: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#CC9AFD',
  600: '#A27CC9',
  700: '#9778C2',
  800: '#8263A1',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const mint: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#8BD8AE',
  600: '#84A988',
  700: '#6AA48A',
  800: '#8263A1',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const yellow: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#FFDA38',
  600: '#84A988',
  700: '#6AA48A',
  800: '#8263A1',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const error = {
  dark: '#FF505B',
  200: '#FF8880',
  400: '#FF505B',
  500: '#FF3844',
  700: '#83343b',
};

const flightCategory = {
  IFR: '#FA0907',
  LIFR: '#F703FE',
  MVFR: '#5460FF',
  VFR: '#1CBD07',
  NA: grey[700],
};
const organ = {
  liver: green[500],
  lungs: blue[500],
  heart: violet[500],
  kidney: '#D7C968',
  unknown: grey[700],
};
/**
 * OCS console artwork colours (TMDXNC-49).
 *
 * Two anchors per role. Fills are FITTED by
 * `scripts/ocs-console-masks/build.py` against the Figma variant renders, not
 * picked by eye — re-run it rather than editing these by hand. Highlights are
 * the existing organ tokens, which the measurement confirmed exactly: the
 * suppressed lungs variant peaks at `blue[500]` to the byte.
 *
 * Heart is genuinely less saturated than the other two organs (0.75 against
 * ~0.96 and 1.0). That is the artwork, not a fitting error — it was verified
 * against the design's own mid-tone clusters, and an earlier assumption that
 * every organ was fully saturated wrongly condemned this value.
 *
 * `technicalHighlight` is `Danger/40`, the one colour on this artwork that is
 * actually bound to a Figma variable (it is also the serial text colour).
 */
const ocsConsole = {
  liverFill: '#8EFF0B',
  liverHighlight: green[500],
  heartFill: '#7940FF',
  heartHighlight: violet[500],
  lungsFill: '#0060FF',
  lungsHighlight: blue[500],
  technicalFill: '#FF1250',
  technicalHighlight: '#F05252',
  // The certification pair is the one role whose fill carries its own
  // brightness rather than leaving it to the mask: its layer paints opaque, so
  // the composite IS this colour and there is no alpha left to dim. All four
  // are generated by `scripts/ocs-console-masks/build.py` as a fitted
  // chromaticity times a measured amplitude, then transcribed by hand and
  // pinned in `ocsConsolePalette.test.ts` — re-run the generator rather than
  // hand-tuning a digit here.
  //
  // The suppressed variants are what make an on-case trial console still read
  // as on-case. Same hue and saturation as their active counterparts, and two
  // different derivations for the brightness: the fill sits at the suppressed
  // base pair's own amplitude, the highlight at the same RATIO the fill moved
  // through, because it is a stroke colour rather than a body wash.
  certificationFill: '#4D4400',
  certificationFillSuppressed: '#242000',
  certificationHighlight: yellow[500],
  certificationHighlightSuppressed: '#77661A',
};
const aircraftStatus = {
  available: grey[100],
  notAvailable: grey[700],
  maintenance: error[500],
  aog: error[200],
};
const severity = {
  critical: grey[700],
  major: yellow[500],
  minor: mint[500],
  low: '#FFFFFF',
};

// TMDXNC-68 — Hub Dashboard tokens.
//
// Nearly the whole screen already resolves to existing tokens: the panel fill is
// `mapBlue[800]` (verified against the design render), the header clock is the
// bound Figma variable `Map Blue/300` = `mapBlue[300]`, and the device bricks use
// `palette.organ`, whose liver, lungs and heart are the other three bound variables. Only
// the NOW line has no token yet — the `error` scale's nearest red is `#FF505B`,
// which is a different colour.
//
// The design's 13-state shift vocabulary is deliberately NOT tokenised here: the
// feed carries 3 of those states, the taxonomy is an open upstream question
// (TMDXNC-67), and baking it now would only have to be redone.
const hubDashboard = {
  /** `Danger/40` — the red NOW line down the ruler and the specialist rows. */
  nowTime: '#FF0000',
  nowLine: '#FF000080',
  /**
   * Shift-type colours, read from the design's own legend component
   * (Figma `821:76488`, the `Shift` variant set) rather than inferred.
   *
   * TMDXNC-49 deliberately left these un-tokenised because the values were only
   * visible as unbound swatches and the taxonomy was an open question. The
   * variant set answers the first half, so they land here now; the taxonomy is
   * still TMDXNC-67's to confirm.
   *
   * Three are exactly `palette.organ` and `unavailable` is exactly `nowLine`,
   * which is a useful check that these came from the same source as the rest of
   * the screen.
   *
   * `available`, `noCall` and `travel` all share one translucent grey in the
   * current design. Available is distinguished by a second, brighter layer over
   * it; No Call and Travel are genuinely identical, which is worth a designer
   * question since they mean different things.
   */
  shift: {
    available: 'rgba(217, 217, 217, 0.4)',
    /** Second layer drawn over `available`, which is what lifts it above No Call. */
    availableHighlight: 'rgba(255, 255, 255, 0.8)',
    noCall: 'rgba(217, 217, 217, 0.4)',
    travel: 'rgba(217, 217, 217, 0.4)',
    onCall: '#0E9F6E',
    extraOnCall: '#56AE9F',
    atHub: '#1C64F2',
    phoneSupport: '#6875F5',
    /** Sick leave and vacation share one amber in the design. */
    absence: '#ED9F07',
    unavailable: '#F05252',
  },
};

export const themeBase: Theme = createTheme(
  deepmerge(defaultTheme, {
    // Configure the grid to use 24 columns
    components: {
      MuiGrid: {
        defaultProps: {
          columns: 24, // Set the default number of columns to 24
        },
      },
    },
    palette: {
      ...getPalette({
        mode: 'dark',
        background: '#000',
        primary: grey[200],
        grey,
        blue,
        mapBlue,
        green,
        violet,
        orange,
        mint,
        yellow,
      }),
      error,
      success: {
        dark: '#00E733',
      },
      flightCategory,
      organ,
      ocsConsole,
      aircraftStatus,
      severity,
      hubDashboard,
    },
    typography: {
      blue: {
        color: blue[200],
      },
      green: {
        color: green[200],
      },
      purple: {
        color: violet[200],
      },
      h1: {
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '32px',
        fontWeight: 500,
      },
      h2: {
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '24px',
        fontWeight: 500,
      },
    },
  } as ThemeOptions),
);
